<template>
    <div class="apprentice-simple-card personal-goals">
        <div class="component-title">
            <h5 class="title">{{ $t('common.learning_goals') }}</h5>

            <div
                class="actions"
                v-if="!teacherAccess">
                <b-button
                    class="btn-add btn-add-personal-goal"
                    v-b-tooltip.hover
                    variant="primary-icon-only"
                    :title="$t('common.add_learning_goal')"
                    @click="addPersonalGoal">
                </b-button>
            </div>
        </div>

        <div
            class="not-found with-header"
            v-if="personalGoals && personalGoals.length === 0">
            <div
                class="btn-add-circle icon"
                v-b-tooltip.hover
                v-if="!teacherAccess"
                :title="$t('common.add_learning_goal')"
                @click="addPersonalGoal"></div>
            <div
                class="alert-notice"
                v-if="!teacherAccess">
                {{ $t('personal_goals.you_do_not_have_a_learning_goal_yet') }}
            </div>
            <div
                class="alert-notice"
                v-if="teacherAccess">
                {{ $t('personal_goals.no_learning_goals_have_been_set') }}
            </div>
        </div>

        <design-2021-personal-goal-component
            v-for="goal in goals"
            v-else
            :personal-goal-in="goal"
            :teacher-access="teacherAccess"
            :skill-trees-in="skillTreesIn"
            :categories-in="categoriesIn"
            :seasons-in="seasonsIn"
            :key="`${goal.id}-${goal.name.toLowerCase()}`"
            @personal-goal-overview="showPersonalGoalOverview"
            @new-profile-card-from-personal-goal="createProfileCardFromPersonalGoal">
        </design-2021-personal-goal-component>

        <design-2021-personal-goal-overview-modal-component
            :teacher-access="teacherAccess"
            :badges-in="badges"
            :skill-trees-in="skillTreesIn"
            ref="personal-goal-overview-modal"
            @personal-goal-updated="updatedPersonalGoal"
            @personal-goal-tasks-removed="removePersonalGoalTask"
            @update-badge="updateBadge"
            @show-profile-card-from-personal-goal-modal="createProfileCardFromPersonalGoal">
        </design-2021-personal-goal-overview-modal-component>

        <design-2021-personal-goal-add-task-modal-component
            ref="add-personal-goal-task"
            @task-added="openPersonalGoalOverviewModal">
        </design-2021-personal-goal-add-task-modal-component>

        <design-2021-personal-goal-add-personal-goal-modal-component
            ref="personal-goal-add-modal"
            :skill-trees-in="skillTreesIn"
            @add-task="addPersonalGoalTask">
        </design-2021-personal-goal-add-personal-goal-modal-component>

        <div
            class="show-more"
            v-if="personalGoals.length > 3 && archivedView !== 1">
            <b-button
                class="btn-show-all btn-show-more-modules"
                v-if="personalGoals.length"
                variant="primary"
                @click="showPersonalGoals">
                {{ $t('common.more') }}
            </b-button>
        </div>

        <design-2021-profile-add-card-component
            ref="addProfileCard"
            :categories-in="categoriesIn"
            @add-card="addCard"
            :seasons-in="seasonsIn">
        </design-2021-profile-add-card-component>
    </div>
</template>

<script>
import { ords } from '../../app';
import { routes } from '../../helpers/routes';

const personalGoalsOrds = ords.use('personalGoal');
const badgesOrds = ords.use('badges');

export default {
    name: 'design-2021-personal-goal-widget-component',
    props: {
        PersonalGoalsIn: Array,

        teacherAccess: {
            type: Boolean,
            required: false,
            default: false,
        },
        showAll: {
            type: Number,
            default: 0,
        },
        userCredentialsIn: Object,
        skillTreesIn: {
            type: Array,
            required: true,
            default: () => [],
        },

        archivedView: {
            type: Number,
            required: false,
            default: 0,
        },

        badgesIn: {
            type: Array,
            required: true,
            default: () => [],
        },

        categoriesIn: {
            type: Array,
            required: true,
            default: () => [],
        },

        seasonsIn: {
            type: Array,
            required: true,
            default: () => [],
        },
    },
    data() {
        return {
            personalGoals: [],
            badges: [],
        };
    },

    mounted() {
        personalGoalsOrds.add(this.PersonalGoalsIn);
        badgesOrds.add(this.badgesIn);

        personalGoalsOrds
            .with('tasks', (taskCallback) => {
                taskCallback.with('files');
                taskCallback.with('personal_goal_task_deadline', (deadlineCallback) => {
                    deadlineCallback.with('agenda_item');
                });
            })
            .with('skills')
            .with('badges', (badgesCallback) => {
                badgesCallback.with('file');
            })
            .where('archived', '=', 0)
            .orderBy('created_at', 'desc')
            .get()
            .subscribe((personalGoals) => {
                this.personalGoals = personalGoals;
            });

        badgesOrds
            .with('file')
            .get()
            .subscribe((badges) => {
                this.badges = badges;
            });
    },

    methods: {
        addPersonalGoal() {
            this.$refs['personal-goal-add-modal'].show();
        },

        showPersonalGoalOverview(selectedPersonalGoal) {
            const personalGoal = this.goals.find((goal) => goal.id === selectedPersonalGoal.id);
            this.$refs['personal-goal-overview-modal'].show(personalGoal);
        },

        showPersonalGoals() {
            if (!this.teacherAccess) {
                window.location.href = this.routes.personalGoals();
            } else {
                window.location.href = this.routes.personalGoalsStudent(this.userCredentialsIn.id);
            }
        },

        archivePersonalGoal(goal) {
            this.alert.saving();

            this.axios.post(this.routes.personalGoalArchive(goal.id)).then(
                (response) => {
                    goal.update({ archived: 1 });

                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        showUpdatePersonalGoalModal(goal) {
            this.$refs['personal-goal-overview-modal'].show(goal);
        },

        removePersonalGoal(goal) {
            this.alert
                .confirm(
                    this.$t('common.delete_learning_goal'),
                    this.$t('personal_goals.are_you_sure_you_want_to_delete_this_learning_goal'),
                )
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }
                    this.alert.saving();
                    this.axios.delete(this.routes.personalGoalRemove(goal.id)).then(
                        (response) => {
                            goal.remove();
                            this.alert.success();
                        },
                        (error) => {
                            return this.alert.ajaxError(error);
                        },
                    );
                });
        },

        updatedPersonalGoal(personalGoalUpdated) {
            for (let i = 0; i < this.goals.length; i++) {
                if (this.goals[i].id === personalGoalUpdated.id) {
                    this.goals[i].update(personalGoalUpdated);

                    ords.hold();
                    ords.use('personalGoal').detach(this.goals[i].id, 'skills');

                    personalGoalUpdated.skills.forEach((skill) => {
                        this.goals[i].addRelation('skill', skill);
                        this.goals[i].attach('skills', skill.id);
                    });
                    ords.continue();
                }
            }
        },

        addPersonalGoalTask(goal) {
            this.$refs['add-personal-goal-task'].show(goal);
        },

        updateTaskCheckedState(goalId, task, checked) {
            const data = {
                checked: checked,
            };

            this.axios.post(this.routes.personalGoalTaskStatusUpdate(task.id), data).then(
                (response) => {
                    task.update(response.data);
                },
                (error) => {
                    return this.alert.ajaxError(error);
                },
            );
        },

        getPersonalGoalTasksStatus(goal) {
            let status = '';
            const stats = goal.getPersonalGoalTasksStatuses();

            if (stats.finished === 0 && stats.unfinished === 0) {
                status = this.$t('personal_goals.this_learning_goal_has_no_tasks');
            } else if (stats.finished === goal.tasks.length) {
                status = this.$t('personal_goals.all_tasks_completed');
            } else if (stats.finished > 0) {
                status = `${stats.finished} van de ${goal.tasks.length} taken voltooid`;
            } else {
                status = this.$t('personal_goals.no_tasks_completed');
            }
            return status;
        },

        removePersonalGoalTask(removedTask) {
            let personalGoalToBeEdit = this.goals.find((goal) => goal.id === removedTask.personal_goal_id);

            for (let i = 0; i < personalGoalToBeEdit.tasks.length; i++) {
                if (personalGoalToBeEdit.tasks[i].id === removedTask.id) {
                    personalGoalToBeEdit.tasks[i].remove();
                }
            }
        },

        updateBadge(updatedPersonalGoal) {
            let existingPersonalGoal = this.personalGoals.find(
                (personalGoal) => personalGoal.id === updatedPersonalGoal.id,
            );
            if (existingPersonalGoal) {
                if (existingPersonalGoal.badges.length > 0) {
                    existingPersonalGoal.badges[0].remove();
                }

                if (updatedPersonalGoal.badges.length > 0) {
                    existingPersonalGoal.addRelation('badges', updatedPersonalGoal.data.badges[0]);
                    existingPersonalGoal.attach('badges', updatedPersonalGoal.data.badges[0].id);
                }
            }
        },

        openPersonalGoalOverviewModal(PersonalGoalTask) {
            const personalGoal = this.goals.find((goal) => goal.id === PersonalGoalTask.personal_goal_id);
            this.$refs['personal-goal-overview-modal'].show(personalGoal);
        },

        createProfileCardFromPersonalGoal(cardData) {
            this.$refs.addProfileCard.show(cardData);
        },

        addCard(cardData) {
            this.alert.saving();
            this.axios.post(this.routes.profileCard(), cardData).then(
                (response) => {
                    this.alert.success();
                    window.location.href = this.routes.profileViewCard(response.data.id);
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
    },

    computed: {
        goals() {
            if (this.archivedView === 0 && this.personalGoals.length > 3) {
                return this.personalGoals.slice(0, 3);
            }

            return this.personalGoals;
        },
    },
};
</script>

<style scoped lang="scss">
.show-more {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');

    .btn-show-more-modules {
        font-weight: $font-weight-bold;
    }
}
</style>
